import isLooselySameValue from '../../../used-vue/api/used-search-options/IsLooselySameValue';
import isSameArray from '../../../used-vue/api/used-search-options/IsSameArray';
import fillMissingKeysWithDefaultValues from '../../../used-vue/api/used-search-options/FillMissingKeysWithDefaultValues';
import getDefaultUserOptions from '../../api/DefaultValues';
import getDefaultOptions from '../../api/DefaultOptions';
import getAvailableUserOptionKeys from '../../../used-vue/api/used-search-options/AvailableKeys';
import debounce from 'lodash/debounce';

require('../../../modules/polyfills/object-assign');
const TWEEN = require('@tweenjs/tween.js');

let loadingTimeout;
let animatedCountTween;
let animationFrame;
let animatedCountDestination;
const animate = function(currentTime) {
    animationFrame = requestAnimationFrame(animate);
    TWEEN.update();
}

const haveOptionsChanged = function(originalOptions, newOptions) {
    let somethingChanged = false;
    // Step through each of the user options that, if changed, mean the search results are invalid
    validUserOptions.forEach((key) => {
        if (somethingChanged == true) {
            return;
        }

        if (typeof originalOptions[key] !== 'undefined' && originalOptions[key].length == 0) {
            if (typeof newOptions[key] !== 'undefined' && newOptions[key].length > 0) {
                somethingChanged = true;
                return;
            }
            return;
        }

        if (typeof originalOptions[key] == 'object') {
            if (!isSameArray(originalOptions[key], newOptions[key])) {
                somethingChanged = true;
                return;
            }
            return;
        }

        if (!isLooselySameValue(originalOptions[key], newOptions[key])) {
            somethingChanged = true;
            return;
        }
    });
    return somethingChanged;
};

const validUserOptions = getAvailableUserOptionKeys();

const state = {
    lastResultsUrl: false,
    results: [],
    resultsCollected: false,
    haveResults: false,
    pages: [],
    options: getDefaultOptions(),
    animatedCount: 0,
    userOptions: getDefaultUserOptions(),
    loading: true,
    resultsLoading: true,
    loadingTimeout: false,
    firstLoad: true,
    usedDealers: [],
};

// getters
const getters = {
    count(state) {
        if (state.options.count) {
            return state.options.count;
        }
        return 0;
    },
    searchType(state) {
        if (state.userOptions.searchType) {
            return state.userOptions.searchType;
        }
        return 0;
    },
    preReg(state) {
        return state.userOptions.preReg;
    },
    preRegId(state) {
        return state.userOptions.preRegId;
    },
    models(state) {
        if (state.options.model) {
            return state.options.model;
        }
        return [];
    },
    manufacturers(state) {
        if (state.options.manufacturer) {
            return state.options.manufacturer;
        }
        return [];
    },
    userOptions(state) {
        if (state.userOptions) {
            return state.userOptions;
        }
        return {};
    },
    minPrice(state) {
        if (state.options.minPrice) {
            return parseInt(state.options.minPrice);
        }
        return null;
    },
    maxPrice(state) {
        if (state.options.maxPrice) {
            return parseInt(state.options.maxPrice);
        }

        return null;
    },
    maxPrices(state) {
        let prices = [];

        let i = state.options.minPrice < 5000
            ? Math.ceil(state.options.minPrice / 500) * 500
            : Math.ceil(state.options.minPrice  / 1000) * 1000;

        while (i < state.options.maxPrice) {
            prices.push({
                price: i,
                disabled:
                state.options.minPrice !== "" && i <= state.options.minPrice
            });
            i = i < 5000 ? i + 500 : i + 1000;
        }

        return prices;
    },
    loading(state) {
        return state.loading;
    },
};

// actions
const actions = {
    goToSearch({ state }) {
        const query = new URLSearchParams(state.userOptions).toString();
        window.location.href = `/used-vehicles/results?${query}`;
    },
    getOptions: debounce(
        async ({ commit, dispatch, state }) => {
            dispatch('setLoading', true);

            try {
                const res = await axios.post('/ajax/used-vehicles/options', state.userOptions);
                commit('setOptions', res.data);
                dispatch('animateCount', res.data);

            } catch (error) {
                commit('setOptions', []);
                commit('setManufacturers', []);
                throw error;

            } finally {
                dispatch('setLoading', false);
            }
        },
        350,
        { leading: true, trailing: true }
    ),
    updateOptions({ commit, dispatch, state }, options) {
        // If the updateOptions call has the fullOptions property, we're updating the full set of options, not just a single key
        // Since the incoming options might not be a full list of every option, we should recreate any missing keys using the default values
        // This is how we handle loading query parameters on the search results page. The route changes, we watch the route in used-results.vue, and we trigger updateOptions based on the query params
        if (typeof options.fullOptions !== 'undefined') {
            options = fillMissingKeysWithDefaultValues(options, state);
        }

        // Check if the options have changed
        let isChangedOptions = haveOptionsChanged(state.userOptions, options);
        // Update the state of the options
        commit('setUserOptions', options);

        // If the search has changed in some way (excluding page/sort/size)
        // Then reset our results set and start again
        // But if it's the first load, don't reset results, as this would prevent from deep linking to a certain page
        if (!state.firstLoad && !isChangedOptions) {
            commit('resetResults');
        }
        commit('setFirstLoad', false);
        return dispatch('getOptions');
    },

    setLoading({ commit, state }, loading) {
        if (loadingTimeout !== false) {
            clearTimeout(loadingTimeout);
            loadingTimeout = false;
        }

        if (!loading) {
            commit('setLoading', false);
            return;
        }

        loadingTimeout = setTimeout(() => {
            commit('setLoading', true);
        }, 300);
    },
    animateCount({ commit, state }, options) {
        if (typeof options.count == 'undefined') {
            return;
        }
        // If the value of the count has changed, we need to initialize the animation of the number
        if (options.count !== animatedCountDestination) {
            // If we don't have a TWEEN object setup already then set it up
            if (!animatedCountTween) {
                let countObject = {count:0};
                animatedCountTween = new TWEEN.Tween(countObject);
                animatedCountTween.to({count: options.count}, 500)
                .onUpdate(function(countObject) {
                    commit('setAnimatedCount', countObject.count);
                })
                .onComplete(function() {
                    cancelAnimationFrame(animationFrame);
                })
                .onStop(function() {
                    cancelAnimationFrame(animationFrame);
                });
                animatedCountTween.start();
                animatedCountDestination = options.count;

                animate();
                return;
            }
            // Otherwise use the existing TWEEN object
            animatedCountTween.stop();
            animatedCountTween.to({count: options.count}, 500);
            animatedCountTween.start();
            animatedCountDestination = options.count;

            animate();
        }
    },
    resetUserOptions({state, commit}) {
        let options = getDefaultUserOptions();
        delete options.d;
        commit('setUserOptions', options);
        commit('resetOptions');
    },
};

const mutations = {
    setUserOptions(state, options) {
        if (typeof options !== 'object') {
            return false;
        }

        let changed = false;
        Object.keys(options).forEach((key) => {
            if (typeof state.userOptions[key] == 'undefined') {
                return;
            }
            if (key == 'sortBy' && state.userOptions[key] != options[key]) {
                state.sortByModified = true;
            }
            state.userOptions[key] = options[key];
            changed = true;
        });

        return changed;
    },
    setResults(state, results) {
        if (typeof results.data == 'undefined') {
            state.results = [];
            state.haveResults = false;
            state.resultsCollected = '{}';
            return;
        }
        if (typeof results.payment_search == 'undefined' || !results.payment_search) {
            state.results = results.data;
            // Sets the "hash" of the results, used later to determine if we've already collected results
            state.resultsCollected = JSON.stringify(state.userOptions);
            state.haveResults = (state.results.length > 0) ? true : false;
            return;
        }

        // If it's a payment search, we need to combine the used vehicle results and the payment search results together
        let resultsKeyedById = {};
        results.v.forEach(vehicle => {
            resultsKeyedById[vehicle.id] = vehicle;
        });
        let combinedResults = [];
        results.data.forEach(vehicle => {
            const id = vehicle.Vehicle.Physical.ExternalVehicleId || 0;
            if (typeof resultsKeyedById[id] !== 'undefined') {
                combinedResults.push({
                    ...resultsKeyedById[id],
                    payment_search: 1,
                    finance: {
                        FinanceProductResults: vehicle.Finance || []
                    },
                });
            }
        });
        state.results = combinedResults;
        // Sets the "hash" of the results, used later to determine if we've already collected results
        state.resultsCollected = JSON.stringify(state.userOptions);
        state.haveResults = (state.results.length > 0) ? true : false;
    },
    setOptions(state, options) {
        Object.keys(options).forEach(key => {
            if (typeof options[key] !== 'object') {
                return;
            }

            if (!state.userOptions[key]){
                return;
            }

            // Check to see if the currently selected user option is in the list of available options. If it's not then add it to the end of the list of available options, and give it a count of 0
            // By doing this, we can avoid user selection being forgotten when an unavailable combination is selected
            let found = false;

            if (typeof state.userOptions[key] == 'object') {
                state.userOptions[key].forEach(userOption => {
                    // The option will either be an object with the count attached to it, or just a string
                    if (options[key].find(option => {
                        if (typeof option == 'object') {
                            return option[key] == userOption;
                        } else {
                            return option == userOption;
                        }
                    })) {
                        return;
                    }
                    options[key].push({
                        [key]: userOption,
                        count: 0,
                    });
                });
            } else {
                let hasCount = false;
                options[key].forEach(el => {
                    if (typeof el == 'object') {
                        hasCount = true;
                        if (el[key] == state.userOptions[key]) {
                            found = true;
                        }
                        return;
                    }
                    if (el == state.userOptions[key]) {
                        found = true;
                    }
                });
                if (!found) {
                    if (hasCount) {
                        let o = {};
                        o[key] = state.userOptions[key];
                        o.count = 0;
                        options[key].push(o);
                    } else {
                        options[key].push(state.userOptions[key]);
                    }
                }
            }
        });
        state.options = options;
    },
    setCount(state, count) {
        state.options.count = parseInt(count);
    },
    setAnimatedCount(state, count) {
        state.animatedCount = count;
    },
    setLoading(state, loading) {
        state.loading = loading;
    },
    setResultsLoading(state, loading) {
        state.resultsLoading = loading;
    },
    setFirstLoad(state, v) {
        state.firstLoad = v;
    },
    setUsedDealers(state, usedDealers) {
        state.usedDealers = usedDealers;
    },
    setModels(state, models) {
        state.options.model = models;
    },
    setManufacturers(state, manufacturers) {
        state.options.manufacturer = manufacturers;
    },
    resetOptions(state) {
        state.options = getDefaultOptions();
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};